import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    Button,
    ButtonGroup,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
    useDataProvider,
    useTranslate,
    useNotify,
    downloadCSV,
    Title,
} from 'react-admin';
import jsonExport from 'jsonexport/dist';
import { useCanAccess } from '@react-admin/ra-rbac';
import { useQuery } from '@tanstack/react-query';
import { LineChart } from '@mui/x-charts/LineChart';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { useDrawingArea, useXScale } from '@mui/x-charts/hooks';
import { NewUsersHistoryItem } from './statistics/types';
import {
    getFinnishHolidays,
    HolidayPeriod,
} from './statistics/finnishHolidays';

type GroupBy = 'day' | 'month';
type RangeMode = 'schoolyear' | 'custom';
type MetricKey = 'total' | 'mpass' | 'finnlectura';

type SeriesItem = {
    label: string;
    color: string;
    data: number[];
    showMark?: boolean;
};

const APP_META = [
    {
        name: 'opepalvelu',
        label: 'Opettajat',
        colors: { total: '#1976d2', mpass: '#64b5f6', finnlectura: '#0d47a1' },
    },
    {
        name: 'opiskelija',
        label: 'Opiskelijat',
        colors: { total: '#2e7d32', mpass: '#81c784', finnlectura: '#1b5e20' },
    },
    {
        name: 'oppilas',
        label: 'Oppilaat',
        colors: { total: '#ed6c02', mpass: '#ffb74d', finnlectura: '#e65100' },
    },
] as const;

const METRIC_META: Record<MetricKey, { label: string; totalColor: string }> = {
    total: { label: 'Kaikki', totalColor: '#616161' },
    mpass: { label: 'MPASS', totalColor: '#757575' },
    finnlectura: { label: 'Finnlectura', totalColor: '#424242' },
};

const HolidayBands = ({
    periods,
    dates,
}: {
    periods: HolidayPeriod[];
    dates: string[];
}) => {
    const { left, top, width, height } = useDrawingArea();
    const xScale = useXScale() as any;
    if (!dates.length || typeof xScale !== 'function') return null;
    const step = typeof xScale.step === 'function' ? xScale.step() : 0;
    const right = left + width;

    return (
        <g>
            {periods.map(p => {
                const first = dates.find(d => d >= p.start && d <= p.end);
                if (!first) return null;
                let last = first;
                for (let i = dates.length - 1; i >= 0; i--) {
                    if (dates[i] >= p.start && dates[i] <= p.end) {
                        last = dates[i];
                        break;
                    }
                }
                const xl = xScale(first);
                const xr = xScale(last);
                if (xl == null || xr == null) return null;
                const x = Math.max(left, xl - step / 2);
                const w = Math.min(right, xr + step / 2) - x;
                if (w <= 0) return null;
                return (
                    <g key={p.key}>
                        <rect
                            x={x}
                            y={top}
                            width={w}
                            height={height}
                            fill={p.color}
                        />
                        <text
                            x={x + 2}
                            y={top + 4}
                            fontSize={9}
                            fill="#546e7a"
                            opacity={0.85}
                            transform={`rotate(90 ${x + 2} ${top + 4})`}
                        >
                            {p.label}
                        </text>
                    </g>
                );
            })}
        </g>
    );
};

const HoverCapture = ({
    dates,
    onHover,
}: {
    dates: string[];
    onHover: (index: number | null) => void;
}) => {
    const { left, top, width, height } = useDrawingArea();
    const xScale = useXScale() as any;
    if (!dates.length || typeof xScale !== 'function') return null;
    const positions = dates.map(d => xScale(d) as number);

    const handleMove = (e: React.MouseEvent<SVGRectElement>) => {
        const svg = e.currentTarget.ownerSVGElement;
        if (!svg) return;
        const rect = svg.getBoundingClientRect();
        const vb = svg.viewBox?.baseVal;
        const scaleX = vb && vb.width && rect.width ? vb.width / rect.width : 1;
        const x = (e.clientX - rect.left) * scaleX;
        let best = 0;
        let bestDist = Infinity;
        for (let i = 0; i < positions.length; i++) {
            const dist = Math.abs(positions[i] - x);
            if (dist < bestDist) {
                bestDist = dist;
                best = i;
            }
        }
        onHover(best);
    };

    return (
        <rect
            x={left}
            y={top}
            width={width}
            height={height}
            fill="transparent"
            style={{ pointerEvents: 'all' }}
            onMouseMove={handleMove}
            onMouseLeave={() => onHover(null)}
        />
    );
};

const schoolYearStart = (d: Date) => {
    const y = d.getFullYear();
    return d.getMonth() + 1 >= 8 ? new Date(y, 7, 1) : new Date(y - 1, 7, 1);
};

const toInputDate = (d: Date) => {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const NewUsersPage = () => {
    const dataProvider = useDataProvider();
    const translate = useTranslate();
    const notify = useNotify();
    const { canAccess } = useCanAccess({
        action: 'show',
        resource: 'activeusers',
    });

    const now = new Date();
    const currentSy = schoolYearStart(now).getFullYear();
    const [rangeMode, setRangeMode] = useState<RangeMode>('schoolyear');
    const [groupBy, setGroupBy] = useState<GroupBy>('day');
    const [syYear, setSyYear] = useState<number>(currentSy);
    const [visibleApps, setVisibleApps] = useState<Record<string, boolean>>({
        opepalvelu: true,
        opiskelija: true,
        oppilas: true,
        total: true,
    });
    const [visibleMetrics, setVisibleMetrics] = useState<
        Record<MetricKey, boolean>
    >({
        total: true,
        mpass: true,
        finnlectura: true,
    });
    const [showHolidays, setShowHolidays] = useState<boolean>(true);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const hoverSource = useRef<'chart' | 'table' | null>(null);
    const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
    const [customFrom, setCustomFrom] = useState<string>(
        toInputDate(schoolYearStart(now))
    );
    const [customTo, setCustomTo] = useState<string>(toInputDate(now));

    const { from, to } = useMemo(() => {
        if (rangeMode === 'custom') {
            const f = new Date(`${customFrom}T00:00:00Z`);
            const t = new Date(`${customTo}T00:00:00Z`);
            if (
                !customFrom ||
                !customTo ||
                isNaN(f.getTime()) ||
                isNaN(t.getTime())
            ) {
                return { from: undefined, to: undefined };
            }
            return { from: customFrom, to: customTo };
        }
        return {
            from: `${syYear}-08-01`,
            to: `${syYear + 1}-07-31`,
        };
    }, [rangeMode, customFrom, customTo, syYear]);

    const params = `filter=${JSON.stringify({ from, to, group_by: groupBy })}`;
    const { data, isPending, error } = useQuery<NewUsersHistoryItem[]>({
        queryKey: ['newusershistory', params],
        queryFn: () =>
            (dataProvider as any)
                .getAny('/statistics/newusers/history', params)
                .then((res: any) => res.data ?? res),
        enabled: canAccess === true && !!from && !!to,
        staleTime: Infinity,
    });

    const { dates, series } = useMemo(() => {
        const rows = data || [];
        const metricKeys = Object.keys(METRIC_META) as MetricKey[];
        const datesLocal = Array.from(new Set(rows.map(d => d.date))).sort();
        const byKey = new Map<string, NewUsersHistoryItem>();
        const allowedApps = new Set<string>(APP_META.map(a => a.name));
        const rowsByDate = new Map<string, NewUsersHistoryItem[]>();
        rows.forEach(r => byKey.set(`${r.date}|${r.app}`, r));
        rows.forEach(r => {
            if (!r.app || !allowedApps.has(r.app)) return;
            const list = rowsByDate.get(r.date) || [];
            list.push(r);
            rowsByDate.set(r.date, list);
        });

        const seriesLocal: SeriesItem[] = [];
        APP_META.forEach(appMeta => {
            if (!visibleApps[appMeta.name]) return;
            metricKeys.forEach(metric => {
                if (!visibleMetrics[metric]) return;
                seriesLocal.push({
                    label: `${appMeta.label} (${METRIC_META[metric].label})`,
                    color: appMeta.colors[metric],
                    showMark: false,
                    data: datesLocal.map(dt => {
                        const row = byKey.get(`${dt}|${appMeta.name}`);
                        return Number(row?.[metric] || 0);
                    }),
                });
            });
        });

        if (visibleApps.total) {
            metricKeys.forEach(metric => {
                if (!visibleMetrics[metric]) return;
                seriesLocal.push({
                    label: `Yhteensä (${METRIC_META[metric].label})`,
                    color: METRIC_META[metric].totalColor,
                    showMark: false,
                    data: datesLocal.map(dt =>
                        (rowsByDate.get(dt) || []).reduce(
                            (sum, row) => sum + Number(row[metric] || 0),
                            0
                        )
                    ),
                });
            });
        }

        return { dates: datesLocal, series: seriesLocal };
    }, [data, visibleApps, visibleMetrics]);

    const holidayPeriods = useMemo(() => {
        if (groupBy !== 'day' || !dates.length) return [];
        return getFinnishHolidays(dates[0], dates[dates.length - 1]);
    }, [groupBy, dates]);

    const handleChartHover = useCallback((index: number | null) => {
        hoverSource.current = 'chart';
        setHoveredIndex(index);
    }, []);

    useEffect(() => {
        if (hoverSource.current !== 'chart' || hoveredIndex === null) return;
        rowRefs.current[hoveredIndex]?.scrollIntoView({
            block: 'nearest',
            behavior: 'smooth',
        });
    }, [hoveredIndex]);

    const syOptions = useMemo(
        () =>
            Array.from(
                { length: Math.max(0, currentSy - 2025 + 1) },
                (_, i) => currentSy - i
            ),
        [currentSy]
    );

    const handleExport = () => {
        const dateHeader = translate('statistics.newusershistory.date');
        const rows = dates.map((d, i) => {
            const row: Record<string, string | number> = { [dateHeader]: d };
            series.forEach(s => {
                row[s.label] = s.data[i] ?? 0;
            });
            return row;
        });
        jsonExport(rows, { rowDelimiter: ';' }, (err, csv) => {
            if (err) {
                notify(translate('notifications.EXPORT_ERROR'), {
                    type: 'error',
                });
                return;
            }
            const suffix =
                rangeMode === 'custom'
                    ? `${customFrom}_${customTo}`
                    : `${syYear}-${syYear + 1}`;
            downloadCSV(`\ufeff${csv}`, `new-users-${suffix}-${groupBy}`);
        });
    };

    if (!canAccess) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {translate('statistics.noaccess')}
            </Alert>
        );
    }

    const hasData = !!(data && data.length > 0);

    return (
        <Box sx={{ p: 2 }}>
            <Title title={translate('statistics.newusershistory.title')} />
            <Typography
                variant="h6"
                gutterBottom
                color="text.secondary"
                sx={{ fontWeight: 'normal' }}
            >
                {translate('statistics.newusershistory.title')}
            </Typography>
            <Card>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        p: 1.5,
                        bgcolor: 'grey.50',
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    <FormControl
                        variant="outlined"
                        size="small"
                        fullWidth={false}
                        sx={{ minWidth: 150 }}
                    >
                        <InputLabel id="range-mode-label">
                            {translate('statistics.newusershistory.range')}
                        </InputLabel>
                        <Select
                            labelId="range-mode-label"
                            value={rangeMode}
                            label={translate(
                                'statistics.newusershistory.range'
                            )}
                            onChange={e =>
                                setRangeMode(e.target.value as RangeMode)
                            }
                        >
                            <MenuItem value="schoolyear">
                                {translate(
                                    'statistics.newusershistory.schoolyear'
                                )}
                            </MenuItem>
                            <MenuItem value="custom">
                                {translate('statistics.newusershistory.custom')}
                            </MenuItem>
                        </Select>
                    </FormControl>

                    {rangeMode === 'schoolyear' ? (
                        <FormControl
                            variant="outlined"
                            size="small"
                            fullWidth={false}
                            sx={{ minWidth: 130 }}
                        >
                            <InputLabel id="sy-label">
                                {translate(
                                    'statistics.newusershistory.schoolyear'
                                )}
                            </InputLabel>
                            <Select
                                labelId="sy-label"
                                value={syYear}
                                label={translate(
                                    'statistics.newusershistory.schoolyear'
                                )}
                                onChange={e =>
                                    setSyYear(Number(e.target.value))
                                }
                            >
                                {syOptions.map(y => (
                                    <MenuItem key={y} value={y}>
                                        {`${y}–${y + 1}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <>
                            <TextField
                                type="date"
                                size="small"
                                fullWidth={false}
                                label={translate(
                                    'statistics.newusershistory.from'
                                )}
                                InputLabelProps={{ shrink: true }}
                                value={customFrom}
                                onChange={e => setCustomFrom(e.target.value)}
                                sx={{ width: 160 }}
                            />
                            <TextField
                                type="date"
                                size="small"
                                fullWidth={false}
                                label={translate(
                                    'statistics.newusershistory.to'
                                )}
                                InputLabelProps={{ shrink: true }}
                                value={customTo}
                                onChange={e => setCustomTo(e.target.value)}
                                sx={{ width: 160 }}
                            />
                        </>
                    )}

                    <ButtonGroup variant="contained" size="small">
                        <Button
                            onClick={() => setGroupBy('day')}
                            variant={
                                groupBy === 'day' ? 'contained' : 'outlined'
                            }
                        >
                            {translate('statistics.newusershistory.daily')}
                        </Button>
                        <Button
                            onClick={() => setGroupBy('month')}
                            variant={
                                groupBy === 'month' ? 'contained' : 'outlined'
                            }
                        >
                            {translate('statistics.newusershistory.monthly')}
                        </Button>
                    </ButtonGroup>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                        }}
                    >
                        {[
                            ...APP_META.map(m => ({
                                key: m.name,
                                label: m.label,
                                color: m.colors.total,
                            })),
                            {
                                key: 'total',
                                label: 'Yhteensä',
                                color: '#616161',
                            },
                        ].map(t => (
                            <FormControlLabel
                                key={t.key}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={!!visibleApps[t.key]}
                                        onChange={e =>
                                            setVisibleApps(v => ({
                                                ...v,
                                                [t.key]: e.target.checked,
                                            }))
                                        }
                                        sx={{
                                            color: t.color,
                                            '&.Mui-checked': { color: t.color },
                                        }}
                                    />
                                }
                                label={t.label}
                            />
                        ))}
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                        }}
                    >
                        {(Object.keys(METRIC_META) as MetricKey[]).map(
                            metric => (
                                <FormControlLabel
                                    key={metric}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={!!visibleMetrics[metric]}
                                            onChange={e =>
                                                setVisibleMetrics(v => ({
                                                    ...v,
                                                    [metric]: e.target.checked,
                                                }))
                                            }
                                            sx={{
                                                color: METRIC_META[metric]
                                                    .totalColor,
                                                '&.Mui-checked': {
                                                    color: METRIC_META[metric]
                                                        .totalColor,
                                                },
                                            }}
                                        />
                                    }
                                    label={METRIC_META[metric].label}
                                />
                            )
                        )}
                    </Box>

                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={showHolidays}
                                disabled={groupBy !== 'day'}
                                onChange={e =>
                                    setShowHolidays(e.target.checked)
                                }
                            />
                        }
                        label={translate('statistics.newusershistory.holidays')}
                    />

                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExport}
                        disabled={!hasData}
                        sx={{ ml: 'auto' }}
                    >
                        {translate('statistics.newusershistory.export')}
                    </Button>
                </Box>

                <CardContent>
                    {error ? (
                        <Alert severity="error" sx={{ m: 2 }}>
                            {(error as Error).message}
                        </Alert>
                    ) : !from || !to ? (
                        <Alert severity="info" sx={{ m: 2 }}>
                            {translate('statistics.newusershistory.pickrange')}
                        </Alert>
                    ) : isPending ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                p: 8,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : !hasData ? (
                        <Alert severity="warning" sx={{ m: 2 }}>
                            {translate('statistics.nodata')}
                        </Alert>
                    ) : (
                        <>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                gutterBottom
                            >
                                {translate('statistics.newusershistory.chart')}
                            </Typography>
                            <LineChart
                                height={360}
                                xAxis={[{ data: dates, scaleType: 'point' }]}
                                series={series}
                            >
                                {hoveredIndex !== null &&
                                    dates[hoveredIndex] && (
                                        <ChartsReferenceLine
                                            x={dates[hoveredIndex]}
                                            lineStyle={{
                                                stroke: '#1976d2',
                                                strokeWidth: 1.5,
                                                strokeDasharray: '4 3',
                                                opacity: 0.6,
                                            }}
                                        />
                                    )}
                                {groupBy === 'day' &&
                                    showHolidays &&
                                    holidayPeriods.length > 0 && (
                                        <HolidayBands
                                            periods={holidayPeriods}
                                            dates={dates}
                                        />
                                    )}
                                <HoverCapture
                                    dates={dates}
                                    onHover={handleChartHover}
                                />
                            </LineChart>

                            <Divider sx={{ my: 2 }} />

                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                gutterBottom
                            >
                                {translate('statistics.newusershistory.table')}
                            </Typography>
                            <TableContainer
                                component={Paper}
                                sx={{ maxHeight: 420 }}
                            >
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                {translate(
                                                    'statistics.newusershistory.date'
                                                )}
                                            </TableCell>
                                            {series.map(s => (
                                                <TableCell
                                                    key={s.label}
                                                    align="right"
                                                >
                                                    {s.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dates.map((d, i) => (
                                            <TableRow
                                                key={d}
                                                hover
                                                selected={i === hoveredIndex}
                                                ref={el => {
                                                    rowRefs.current[i] = el;
                                                }}
                                                onMouseEnter={() => {
                                                    hoverSource.current =
                                                        'table';
                                                    setHoveredIndex(i);
                                                }}
                                                onMouseLeave={() =>
                                                    setHoveredIndex(null)
                                                }
                                            >
                                                <TableCell>{d}</TableCell>
                                                {series.map(s => (
                                                    <TableCell
                                                        key={s.label}
                                                        align="right"
                                                    >
                                                        {(
                                                            s.data[i] ?? 0
                                                        ).toLocaleString(
                                                            'fi-FI'
                                                        )}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default NewUsersPage;
